import { RoadMaintenanceFieldsFragment } from 'api/graphql-types';

import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import clsx from 'clsx';

import { getFormattedDate } from 'utils/date';
import { sanitizeAndParseHtml } from 'utils/rich-text-renderer/sanitize-and-parse';

import { BaseModule } from 'modules/base-module/base-module';
import { renderDownloadLink } from 'modules/downloads/downloads';
import { Link } from 'components/link/link';
import { SidebarLink } from 'components/sidebar-link/sidebar-link';
import { Typography } from 'components/typography';

import styles from './road-maintenance-header.module.scss';

interface IRoadMaintenanceHeaderProps {
	module?: RoadMaintenanceFieldsFragment;
}

const RoadMaintenanceHeader = (props: IRoadMaintenanceHeaderProps): React.JSX.Element => {
	const intl = useIntl();
	const router = useRouter();
	const { locale } = router;
	const enableHeader =
		(props.module?.aboutMaintenance?.endDate && props.module?.aboutMaintenance?.startDate) ||
		props.module?.aboutMaintenance?.detour ||
		(props.module?.aboutMaintenance?.contractor && props.module?.aboutMaintenance?.contractorWebsite);

	const isSidebarEnabled = props?.module?.bodyText?.sidebar != null;

	return (
		<BaseModule
			defaultModuleOptions={{
				backgroundColor: props?.module?.aboutMaintenance?.defaultModuleOptions?.backgroundColor || 'white',
				padding: props?.module?.aboutMaintenance?.defaultModuleOptions?.padding || 'medium'
			}}
		>
			<div className={clsx(styles.row, styles.rowWithSidebar)}>
				<div>
					{enableHeader && (
						<div className={styles.wrapper}>
							{props.module?.aboutMaintenance?.startDate && props.module?.aboutMaintenance?.endDate && (
								<div>
									<span className={styles.title}>
										{intl.formatMessage({ id: 'modules.roadMaintenance.header.when' })}:{' '}
									</span>

									<span>
										{getFormattedDate(new Date(props?.module?.aboutMaintenance.startDate), 'dd MMMM yyyy', locale)} -{' '}
										{getFormattedDate(new Date(props?.module?.aboutMaintenance.endDate), 'dd MMMM yyyy', locale)}
									</span>
								</div>
							)}
							{props.module?.aboutMaintenance?.detour && (
								<div>
									<span className={styles.title}>{intl.formatMessage({ id: 'modules.roadMaintenance.detour' })}: </span>
									<span>{sanitizeAndParseHtml(props.module?.aboutMaintenance?.detour)}</span>
								</div>
							)}
							{props.module?.aboutMaintenance?.contractor && props.module?.aboutMaintenance?.contractorWebsite && (
								<div>
									<span className={styles.title}>
										{intl.formatMessage({ id: 'modules.roadMaintenance.contractor' })}{' '}
										{props.module?.aboutMaintenance?.contractor}:{' '}
									</span>
									<Link href={props.module?.aboutMaintenance?.contractorWebsite} variant="tertiary-dark">
										<span className={styles.link}>
											{intl.formatMessage(
												{ id: 'modules.roadMaintenance.website' },
												{ contractor: props.module?.aboutMaintenance?.contractor }
											)}{' '}
										</span>
									</Link>
								</div>
							)}
						</div>
					)}

					{enableHeader && <hr className={styles.divider} />}
					<div className={styles.about}>{sanitizeAndParseHtml(props?.module?.bodyText?.bodyText || '')}</div>
				</div>

				{isSidebarEnabled && (
					<section
						className={clsx(styles.sidebar, !!props.module?.bodyText?.sidebar?.title?.value && styles.withTitle)}
					>
						{props.module?.bodyText?.sidebar?.title?.value && (
							<Typography tag={props.module.bodyText.sidebar.title.tag || 'div'} variant="h4">
								{props.module?.bodyText?.sidebar?.title?.value}
							</Typography>
						)}
						<ul>
							{props.module?.bodyText?.sidebar?.links?.map((link, idx) => (
								<li key={`${idx}-${link?.title}`}>
									<SidebarLink
										href={link?.href?.url || link?.href?.linkUrl}
										description={link?.description}
										title={link?.href?.text || link?.href?.linkText}
									/>
								</li>
							))}
							{props.module?.bodyText?.sidebar?.files?.map((file) => (
								<li key={file?.data.attributes?.hash} className={styles.file}>
									<SidebarLink
										href={file?.data.attributes?.url}
										description={file?.data.attributes?.caption}
										title={renderDownloadLink({
											ext: file.data.attributes?.ext || '',
											size: file?.data.attributes?.size || 0
										})}
									/>
								</li>
							))}
						</ul>
					</section>
				)}
			</div>
		</BaseModule>
	);
};

export { RoadMaintenanceHeader };
